import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Skeleton,
  Button,
} from "@mui/material";
import { Chat, ChatBubbleOutline, Lock } from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import TieredFeatureAccess from "../../../shared-components/TieredFeatureAccess";

const ConversationCardContent = ({ conversation, Icon, iconColor }) => (
  <CardContent>
    <Stack spacing={1.5}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Icon color={iconColor} sx={{ mt: 0.5 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {conversation.title || "Untitled Conversation"}
          </Typography>
        </Box>
      </Box>

      {conversation.firstMessagePreview && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            pl: 4.5,
          }}
        >
          {conversation.firstMessagePreview}
        </Typography>
      )}

      <Stack direction="row" spacing={1} sx={{ pl: 4.5, alignItems: "center" }}>
        <Chip
          label={`${conversation.messageCount} message${conversation.messageCount !== 1 ? "s" : ""}`}
          size="small"
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary">
          {formatDistanceToNow(new Date(conversation.startedAt), {
            addSuffix: true,
          })}
        </Typography>
      </Stack>
    </Stack>
  </CardContent>
);

export const ConversationList = ({
  conversations,
  loading,
  hasMore = false,
  onLoadMore,
}) => {
  const navigate = useNavigate();
  const listRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Preserve scroll position when conversations are updated
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [conversations]);

  const handleLoadMore = () => {
    if (listRef.current) {
      scrollPositionRef.current = listRef.current.scrollTop;
    }
    onLoadMore();
  };

  if (loading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={120} />
        ))}
      </Stack>
    );
  }

  if (!conversations || conversations.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          px: 2,
        }}
      >
        <ChatBubbleOutline
          sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No conversations yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click "New Conversation" to start chatting with Bud
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} ref={listRef}>
      {conversations.map((conversation) => {
        const isAccessible = conversation.isAccessible !== false;

        if (!isAccessible) {
          // Locked conversation - show blurred card with padlock
          return (
            <Box key={conversation.id}>
              <TieredFeatureAccess
                accessOverride={false}
                blur={true}
                disabled={true}
                tooltipMessage="Upgrade to access conversations older than 7 days."
              >
                <Card variant="outlined" sx={{ opacity: 0.6 }}>
                  <ConversationCardContent
                    conversation={conversation}
                    Icon={Lock}
                    iconColor="disabled"
                  />
                </Card>
              </TieredFeatureAccess>
            </Box>
          );
        }

        // Accessible conversation - normal card
        return (
          <Card
            key={conversation.id}
            variant="outlined"
            sx={{
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                boxShadow: 3,
                borderColor: "primary.main",
              },
            }}
            onClick={() =>
              navigate(`/assistant?conversation=${conversation.id}`)
            }
          >
            <ConversationCardContent
              conversation={conversation}
              Icon={Chat}
              iconColor="primary"
            />
          </Card>
        );
      })}

      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button variant="outlined" onClick={handleLoadMore}>
            Load More
          </Button>
        </Box>
      )}
    </Stack>
  );
};
